import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useAdminWebSocketTracking } from '../hooks/useWebSocketTracking';
import EmptyState from '../components/ui-kit/EmptyState';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  MapPin,
  Truck,
  Navigation,
  RefreshCw,
  Filter,
  Phone,
  Package,
  Clock,
  Search,
  X,
  ChevronRight,
  CheckCircle,
  Eye,
  EyeOff,
  User,
  ArrowRight,
  Route
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Couleurs pour différencier les opérations
const ROUTE_COLORS = [
  '#1db584', '#3b82f6', '#ef4444', '#f59e0b', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'
];

// Create colored truck icon
const createTruckIcon = (color) => new L.DivIcon({
  className: 'custom-truck-marker',
  html: `<div style="
    background: ${color};
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  ">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
      <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
    </svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18],
});

const startIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [20, 33],
  iconAnchor: [10, 33],
  popupAnchor: [1, -28],
  shadowSize: [33, 33]
});

const endIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [20, 33],
  iconAnchor: [10, 33],
  popupAnchor: [1, -28],
  shadowSize: [33, 33]
});

// Component to fit bounds
const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0 && map) {
      const timeoutId = setTimeout(() => {
        try {
          // Check if map container still exists
          if (map && map.getContainer()) {
            // animate:false → pas d'animation de zoom : évite le crash Leaflet
            // "_leaflet_pos undefined" (_onZoomTransitionEnd) quand la carte se
            // démonte/re-rend pendant l'animation (polling temps réel).
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12, animate: false });
          }
        } catch (e) {
          // Map might be unmounted, ignore
        }
      }, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [bounds, map]);
  return null;
};

// Status badge colors
import { getStatusLabel as getCentralStatusLabel } from '../constants/statuses';

const getStatusColor = (status) => {
  switch (status) {
    case 'prete_au_chargement':
      return 'bg-teal-100 text-teal-800 border-teal-200';
    case 'en_cours_de_chargement':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'charge_en_route':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getStatusLabel = (status) => {
  return getCentralStatusLabel(status);
};

const TrackingPage = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'lead_ops', 'ops', 'operateur', 'lead_field_ops', 'field_ops'].includes(user?.role);
  
  // WebSocket tracking for real-time updates
  const { 
    positions: wsPositions, 
    isConnected: wsConnected,
  } = useAdminWebSocketTracking();
  
  const [operations, setOperations] = useState([]);
  const [filteredOperations, setFilteredOperations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedOperations, setSelectedOperations] = useState([]); // Multi-select
  const [routes, setRoutes] = useState({}); // OSRM routes cache
  const [showFilters, setShowFilters] = useState(true);
  const [showAllWithCoords, setShowAllWithCoords] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    clientName: '',
    destination: ''
  });
  
  const refreshIntervalRef = useRef(null);
  
  // Update operations with WebSocket positions
  useEffect(() => {
    if (wsPositions && wsPositions.length > 0 && operations.length > 0) {
      setOperations(prevOps => {
        return prevOps.map(op => {
          const wsPosition = wsPositions.find(p => p.operation_id === op.operationId);
          if (wsPosition) {
            return {
              ...op,
              currentLocation: {
                latitude: wsPosition.latitude,
                longitude: wsPosition.longitude,
                speed: wsPosition.speed,
                timestamp: wsPosition.timestamp
              }
            };
          }
          return op;
        });
      });
      setLastUpdate(new Date());
    }
  }, [wsPositions]);

  // Fetch tracking data
  const fetchTrackingData = useCallback(async () => {
    try {
      let url = `${API}/tracking/active/all`;
      const params = new URLSearchParams();
      
      if (!isAdmin && user?.id) {
        params.append('user_id', user.id);
      }
      
      if (filters.status !== 'all') {
        params.append('status_filter', filters.status);
      }
      
      if (isAdmin && showAllWithCoords) {
        params.append('include_all_with_coords', 'true');
      }
      
      // Server-side search
      if (filters.search && filters.search.length >= 2) {
        params.append('search', filters.search);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      const data = response.data;
      
      setOperations(data.operations || []);
      setStats(data.stats || {});
      setLastUpdate(new Date());
      
    } catch (error) {
      console.error('Error fetching tracking data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, user?.id, filters.status, filters.search, showAllWithCoords]);

  useEffect(() => {
    fetchTrackingData();
    refreshIntervalRef.current = setInterval(fetchTrackingData, 30000);
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [fetchTrackingData]);

  // Apply local filters
  useEffect(() => {
    let filtered = [...operations];
    
    // Filter by client name
    if (filters.clientName) {
      const query = filters.clientName.toLowerCase();
      filtered = filtered.filter(op => 
        op.clientName?.toLowerCase().includes(query)
      );
    }
    
    // Filter by destination
    if (filters.destination) {
      const query = filters.destination.toLowerCase();
      filtered = filtered.filter(op => 
        op.departure?.toLowerCase().includes(query) ||
        op.arrival?.toLowerCase().includes(query)
      );
    }
    
    setFilteredOperations(filtered);
    
    // Update map bounds for filtered operations
    const allBounds = [];
    filtered.forEach(op => {
      if (op.currentLocation?.latitude && op.currentLocation?.longitude) {
        allBounds.push([op.currentLocation.latitude, op.currentLocation.longitude]);
      }
      const depCoords = getCoords(op.departureCoordinates);
      const arrCoords = getCoords(op.arrivalCoordinates);
      if (depCoords) allBounds.push(depCoords);
      if (arrCoords) allBounds.push(arrCoords);
    });
    
    if (allBounds.length > 0) {
      setMapBounds(allBounds);
    }
  }, [operations, filters.clientName, filters.destination]);

  // Fetch OSRM route for an operation
  const fetchRoute = async (operationId) => {
    if (routes[operationId]) return routes[operationId];
    
    try {
      const response = await axios.get(`${API}/tracking/route/${operationId}`);
      const routeData = response.data;
      
      setRoutes(prev => ({ ...prev, [operationId]: routeData }));
      return routeData;
    } catch (error) {
      console.warn('Could not fetch route:', error);
      return null;
    }
  };

  // Fetch routes for selected operations
  useEffect(() => {
    selectedOperations.forEach(opId => {
      const op = operations.find(o => o.operationId === opId);
      if (op && op.hasRouteCoordinates && !routes[opId]) {
        fetchRoute(opId);
      }
    });
  }, [selectedOperations, operations]);

  // Handle operation selection
  const toggleOperationSelection = (operationId) => {
    setSelectedOperations(prev => {
      if (prev.includes(operationId)) {
        return prev.filter(id => id !== operationId);
      } else {
        return [...prev, operationId];
      }
    });
  };

  const selectAllVisible = () => {
    const visibleIds = filteredOperations.map(op => op.operationId);
    setSelectedOperations(visibleIds);
    // Fetch routes for all selected
    visibleIds.forEach(id => {
      const op = operations.find(o => o.operationId === id);
      if (op?.hasRouteCoordinates && !routes[id]) {
        fetchRoute(id);
      }
    });
  };

  const clearSelection = () => {
    setSelectedOperations([]);
  };

  // Get color for operation (based on index in selected list)
  const getOperationColor = (operationId) => {
    const index = selectedOperations.indexOf(operationId);
    return index >= 0 ? ROUTE_COLORS[index % ROUTE_COLORS.length] : ROUTE_COLORS[0];
  };

  // Helper function to get coordinates
  const getCoords = (coordObj) => {
    if (!coordObj) return null;
    const lat = coordObj.latitude || coordObj.lat;
    const lng = coordObj.longitude || coordObj.lng;
    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      return [lat, lng];
    }
    return null;
  };

  // Convert OSRM route to positions
  const getRoutePositions = (routeData) => {
    if (!routeData?.geometry?.coordinates) return null;
    return routeData.geometry.coordinates.map(coord => [coord[1], coord[0]]);
  };

  // Calculate progress percentage
  const calculateProgress = (op) => {
    if (!op.currentLocation || !op.departureCoordinates || !op.arrivalCoordinates) return 0;
    
    const depCoords = getCoords(op.departureCoordinates);
    const arrCoords = getCoords(op.arrivalCoordinates);
    const curCoords = [op.currentLocation.latitude, op.currentLocation.longitude];
    
    if (!depCoords || !arrCoords) return 0;
    
    const totalDist = Math.sqrt(
      Math.pow(arrCoords[0] - depCoords[0], 2) + 
      Math.pow(arrCoords[1] - depCoords[1], 2)
    );
    const currDist = Math.sqrt(
      Math.pow(curCoords[0] - depCoords[0], 2) + 
      Math.pow(curCoords[1] - depCoords[1], 2)
    );
    
    return totalDist > 0 ? Math.min(Math.round((currDist / totalDist) * 100), 100) : 0;
  };

  // Reset filters
  const clearFilters = () => {
    setFilters({ search: '', status: 'all', clientName: '', destination: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Navigation className="w-7 h-7 text-emerald-600" />
            Suivi en Direct
          </h1>
          <p className="text-gray-500">
            {filteredOperations.length} opération(s) • 
            {selectedOperations.length > 0 && ` ${selectedOperations.length} sélectionnée(s) •`}
            {lastUpdate && ` Mise à jour: ${lastUpdate.toLocaleTimeString()}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtres
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchTrackingData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search by operation number */}
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Numéro opération</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="BAO-T-..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-9 h-9"
                />
              </div>
            </div>
            
            {/* Filter by client name */}
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Nom utilisateur</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Nom du client..."
                  value={filters.clientName}
                  onChange={(e) => setFilters(prev => ({ ...prev, clientName: e.target.value }))}
                  className="pl-9 h-9"
                />
              </div>
            </div>
            
            {/* Filter by destination */}
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Trajet (départ/arrivée)</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Ville..."
                  value={filters.destination}
                  onChange={(e) => setFilters(prev => ({ ...prev, destination: e.target.value }))}
                  className="pl-9 h-9"
                />
              </div>
            </div>
            
            {/* Filter by status */}
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">État</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Tous les états" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les états</SelectItem>
                  <SelectItem value="prete_au_chargement">Au chargement</SelectItem>
                  <SelectItem value="en_cours_de_chargement">Chargement en cours</SelectItem>
                  <SelectItem value="charge_en_route">En route</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Actions */}
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Actions</Label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearFilters} className="h-9">
                  <X className="w-4 h-4 mr-1" />
                  Reset
                </Button>
                {filteredOperations.length > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={selectedOperations.length > 0 ? clearSelection : selectAllVisible}
                    className="h-9"
                  >
                    {selectedOperations.length > 0 ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-1" />
                        Désélect.
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        Tout suivre
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total || 0}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Truck className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.charge_en_route || 0}</p>
              <p className="text-xs text-gray-500">En route</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Navigation className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.with_location || 0}</p>
              <p className="text-xs text-gray-500">Avec GPS actif</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Route className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.with_route_coords || 0}</p>
              <p className="text-xs text-gray-500">Avec itinéraire</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map Section */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="overflow-hidden">
            <div className="p-3 border-b flex items-center justify-between bg-gray-50">
              <h2 className="font-semibold text-gray-900 text-sm">Carte</h2>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <label className="flex items-center gap-2 text-xs">
                    <Checkbox
                      checked={showAllWithCoords}
                      onCheckedChange={setShowAllWithCoords}
                    />
                    Toutes avec GPS
                  </label>
                )}
                <Badge variant="outline" className="text-xs">
                  {selectedOperations.length} suivie(s)
                </Badge>
              </div>
            </div>
            
            <div className="h-[450px]">
              <MapContainer
                center={[7.539989, -5.547080]}
                zoom={6}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {mapBounds && mapBounds.length > 0 && <FitBounds bounds={mapBounds} />}
                
                {/* Render selected operations with routes */}
                {selectedOperations.map((opId, index) => {
                  const op = operations.find(o => o.operationId === opId);
                  if (!op) return null;
                  
                  const color = ROUTE_COLORS[index % ROUTE_COLORS.length];
                  const routeData = routes[opId];
                  const routePositions = getRoutePositions(routeData);
                  
                  const currentPos = op.currentLocation?.latitude && op.currentLocation?.longitude
                    ? [op.currentLocation.latitude, op.currentLocation.longitude]
                    : null;
                  
                  const depCoords = getCoords(op.departureCoordinates);
                  const arrCoords = getCoords(op.arrivalCoordinates);
                  
                  return (
                    <React.Fragment key={opId}>
                      {/* OSRM Route (real road) */}
                      {routePositions ? (
                        <Polyline
                          positions={routePositions}
                          color={color}
                          weight={5}
                          opacity={0.8}
                        />
                      ) : depCoords && arrCoords && (
                        /* Fallback: straight line */
                        <Polyline
                          positions={currentPos ? [depCoords, currentPos, arrCoords] : [depCoords, arrCoords]}
                          color={color}
                          weight={3}
                          opacity={0.5}
                          dashArray="8, 8"
                        />
                      )}
                      
                      {/* Departure marker */}
                      {depCoords && (
                        <Marker position={depCoords} icon={startIcon}>
                          <Popup>
                            <div>
                              <strong className="text-green-600">Départ</strong>
                              <p className="text-sm">{op.departure}</p>
                              <Badge className="mt-1" style={{ backgroundColor: color, color: 'white' }}>
                                {op.operationNumber}
                              </Badge>
                            </div>
                          </Popup>
                        </Marker>
                      )}
                      
                      {/* Arrival marker */}
                      {arrCoords && (
                        <Marker position={arrCoords} icon={endIcon}>
                          <Popup>
                            <div>
                              <strong className="text-red-600">Arrivée</strong>
                              <p className="text-sm">{op.arrival}</p>
                              {routeData?.distanceKm && (
                                <p className="text-xs text-gray-500">
                                  {routeData.distanceKm} km • ~{Math.round(routeData.durationMinutes / 60)}h
                                </p>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      )}
                      
                      {/* Truck marker */}
                      {currentPos && (
                        <Marker position={currentPos} icon={createTruckIcon(color)}>
                          <Popup>
                            <div className="min-w-[180px]">
                              <div className="font-semibold" style={{ color }}>{op.operationNumber}</div>
                              <p className="text-sm">{op.departure} → {op.arrival}</p>
                              {op.driver?.name && <p className="text-xs text-gray-500">Chauffeur: {op.driver.name}</p>}
                              {op.currentLocation?.speed > 0 && (
                                <p className="text-xs">Vitesse: {Math.round(op.currentLocation.speed)} km/h</p>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      )}
                    </React.Fragment>
                  );
                })}
                
                {/* Show non-selected operations as faded markers if they have coordinates */}
                {filteredOperations
                  .filter(op => !selectedOperations.includes(op.operationId))
                  .map(op => {
                    const depCoords = getCoords(op.departureCoordinates);
                    if (!depCoords) return null;
                    
                    return (
                      <Marker 
                        key={op.operationId}
                        position={depCoords}
                        opacity={0.4}
                        eventHandlers={{
                          click: () => toggleOperationSelection(op.operationId)
                        }}
                      >
                        <Popup>
                          <div>
                            <p className="font-medium">{op.operationNumber}</p>
                            <p className="text-sm text-gray-500">{op.departure} → {op.arrival}</p>
                            <Button 
                              size="sm" 
                              className="mt-2 w-full"
                              onClick={() => toggleOperationSelection(op.operationId)}
                            >
                              Suivre
                            </Button>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
              </MapContainer>
            </div>
            
            {/* Legend */}
            {selectedOperations.length > 0 && (
              <div className="p-3 bg-gray-50 border-t">
                <p className="text-xs font-medium text-gray-700 mb-2">Légende des itinéraires</p>
                <div className="flex flex-wrap gap-3">
                  {selectedOperations.map((opId, index) => {
                    const op = operations.find(o => o.operationId === opId);
                    if (!op) return null;
                    return (
                      <div key={opId} className="flex items-center gap-2">
                        <div 
                          className="w-4 h-1 rounded"
                          style={{ backgroundColor: ROUTE_COLORS[index % ROUTE_COLORS.length] }}
                        />
                        <span className="text-xs text-gray-600">{op.operationNumber}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>

          {/* Progress Timeline (like La Poste) */}
          {selectedOperations.length > 0 && (
            <Card className="p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Route className="w-5 h-5 text-emerald-600" />
                Progression des livraisons
              </h3>
              <div className="space-y-4">
                {selectedOperations.map((opId, index) => {
                  const op = operations.find(o => o.operationId === opId);
                  if (!op) return null;
                  
                  const color = ROUTE_COLORS[index % ROUTE_COLORS.length];
                  const progress = calculateProgress(op);
                  const routeData = routes[opId];
                  
                  return (
                    <div key={opId} className="relative">
                      {/* Operation header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: color }}
                          />
                          <span className="font-medium text-sm">{op.operationNumber}</span>
                          <Badge className={getStatusColor(op.status)}>
                            {getStatusLabel(op.status)}
                          </Badge>
                        </div>
                        {routeData?.distanceKm && (
                          <span className="text-xs text-gray-500">
                            {routeData.distanceKm} km
                          </span>
                        )}
                      </div>
                      
                      {/* Progress bar with route */}
                      <div className="relative">
                        {/* Background line */}
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-500"
                            style={{ 
                              width: `${progress}%`,
                              backgroundColor: color
                            }}
                          />
                        </div>
                        
                        {/* Points */}
                        <div className="flex items-center justify-between mt-1">
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full bg-green-500" />
                            <span className="text-xs text-gray-600 truncate max-w-[120px]">{op.departure}</span>
                          </div>
                          
                          {/* Truck position indicator */}
                          {op.currentLocation && (
                            <div 
                              className="absolute top-0 transform -translate-x-1/2"
                              style={{ left: `${progress}%` }}
                            >
                              <Truck className="w-4 h-4" style={{ color }} />
                            </div>
                          )}
                          
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-600 truncate max-w-[120px] text-right">{op.arrival}</span>
                            <div className="w-3 h-3 rounded-full bg-red-500" />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>

        {/* Operations List */}
        <div className="lg:col-span-1">
          <Card className="h-fit">
            <div className="p-3 border-b flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 text-sm">Opérations</h2>
              <Badge variant="outline">{filteredOperations.length}</Badge>
            </div>
            
            <ScrollArea className="h-[600px]">
              <div className="p-2 space-y-2">
                {filteredOperations.length === 0 ? (
                  <EmptyState
                    icon={MapPin}
                    title="Aucune opération avec GPS"
                    description="Les opérations apparaîtront ici dès qu'elles seront en cours et que le suivi GPS sera actif."
                    variant="compact"
                  />
                ) : (
                  filteredOperations.map((op) => {
                    const isSelected = selectedOperations.includes(op.operationId);
                    const color = isSelected ? getOperationColor(op.operationId) : null;
                    
                    return (
                      <div
                        key={op.operationId}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-2 bg-white shadow-sm' 
                            : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50'
                        }`}
                        style={isSelected ? { borderColor: color } : {}}
                        onClick={() => toggleOperationSelection(op.operationId)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleOperationSelection(op.operationId)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div>
                              <p className="font-medium text-sm" style={isSelected ? { color } : {}}>
                                {op.operationNumber}
                              </p>
                              {op.clientName && (
                                <p className="text-xs text-gray-500">{op.clientName}</p>
                              )}
                            </div>
                          </div>
                          <Badge className={`text-xs ${getStatusColor(op.status)}`}>
                            {getStatusLabel(op.status)}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
                          <MapPin className="w-3 h-3 text-green-500" />
                          <span className="truncate flex-1">{op.departure}</span>
                          <ArrowRight className="w-3 h-3" />
                          <MapPin className="w-3 h-3 text-red-500" />
                          <span className="truncate flex-1">{op.arrival}</span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          {op.driver?.name && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {op.driver.name}
                            </span>
                          )}
                          <div className="flex items-center gap-1">
                            {op.hasCurrentLocation && (
                              <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-600 border-emerald-200">
                                <Navigation className="w-3 h-3 mr-1" />
                                GPS
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TrackingPage;
